import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  DataContextBuilder,
  ElderChipListSelectComponent,
  ElderDataToolbarComponent,
  ElderFormFieldDenseDirective,
  ElderFormFieldNoHintDirective,
  ElderIntervalInputComponent,
  ElderIntervalPickerBindingDirective,
  ElderIntervalPickerToggleComponent,
  ElderMultiSelectChipsComponent,
  ElderNumberCellDirective,
  ElderQuantityPipe,
  ElderQuantityService,
  ElderSelectChipAvatarDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderToolbarColumnDirective,
  ElderToolbarContentDirective,
  Filter,
  IDataContext,
  Interval,
  Quantity,
  SelectionModelPopupDirective,
  Sort,
} from '@elderbyte/ngx-starter'
import { WorkCenterUtilizationReport } from '../work-center-utilization-report'
import { UtilizationReportService } from '../utilization-report.service'
import { Facility } from '../../../facilities/facility'
import { WorkCenter } from '../../../shopfloor/workcenters/work-center'
import { BehaviorSubject, Observable } from 'rxjs'
import { MexusContext } from '../../../context/mexus-context.service'
import { ProcessPlanService } from '../../../process/process-plans/process-plan.service'
import { ProcessPlan } from '../../../process/process-plans/process-plan'
import { WorkItemFilter } from '../../../orders/work-items/work-item.service'
import { WorkCenterService } from '../../../shopfloor/workcenters/work-center.service'
import { sum } from '../../../../../functional.util'
import { roundQuantity } from '../../reports.util'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatIcon } from '@angular/material/icon'
import { WorkCenterBrowserComponent } from '../../../shopfloor/workcenters/work-center-browser/work-center-browser.component'
import { MatCheckbox } from '@angular/material/checkbox'
import { FormsModule } from '@angular/forms'
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import { FacilityContextDirective } from '../../../context/facility-context/facility-context.directive'
import { FacilityContextMenuComponent } from '../../../context/facility-context/facility-context-menu/facility-context-menu.component'
import { AsyncPipe } from '@angular/common'

export enum EvalVariant {
  DEFAULT = 'DEFAULT',
  BYDAY = 'BYDAY',
}

@Component({
  selector: 'app-utilization-report-table',
  templateUrl: './utilization-report-table.component.html',
  styleUrls: ['./utilization-report-table.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    FacilityContextMenuComponent,
    FacilityContextDirective,
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    MatFormField,
    ElderFormFieldDenseDirective,
    MatLabel,
    ElderIntervalInputComponent,
    FormsModule,
    MatCheckbox,
    ElderFormFieldNoHintDirective,
    ElderMultiSelectChipsComponent,
    SelectionModelPopupDirective,
    WorkCenterBrowserComponent,
    ElderSelectChipAvatarDirective,
    MatIcon,
    MatSuffix,
    ElderChipListSelectComponent,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatSortHeader,
    ElderNumberCellDirective,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
    ElderIntervalPickerBindingDirective,
    ElderIntervalPickerToggleComponent,
  ],
})
export class UtilizationReportTableComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  public readonly data: IDataContext<WorkCenterUtilizationReport>

  private readonly _evalVariant$ = new BehaviorSubject<EvalVariant>(EvalVariant.DEFAULT)
  public readonly EvalVariant = EvalVariant

  @Input()
  public workCenterId: string
  public facilityId: string

  public showEvalElements = true
  public involvedProcessPlans$: Observable<ProcessPlan[]>
  private _workCenters: WorkCenter[] = []
  public totalPieceQuantity: number
  public totalItemQuantity: number
  public totalWeight: Quantity
  selection: WorkCenterUtilizationReport[]
  private reportService = inject(UtilizationReportService)
  workCenterService = inject(WorkCenterService)
  private quantityService = inject(ElderQuantityService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const processPlanService = inject(ProcessPlanService)

    this.data = DataContextBuilder.start<WorkCenterUtilizationReport>()
      .localApply((r) => this.buildRows(r))
      .autoStartOnFiltersAll(
        ['completionInterval', 'manufactureFacility'], // TODO: use WorkItemFilter
        [new Sort('workCenterNumber', 'asc'), new Sort('date', 'asc')]
      )
      .build(this.reportService)

    this.data.data.subscribe((records) => this.calculateTotals(records))

    this.involvedProcessPlans$ = processPlanService.processPlans$

    this.reportService.completionInterval$.subscribe({
      next: (c) => {
        this.filter = new Filter('completionInterval', c.toInstantString())
      },
      error: (error) => this.log.error('Failed to set completion filter!', error),
    })

    this._evalVariant$.subscribe(() => {
      if (this.data.isStarted) {
        this.data.reload()
      }
    })
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set facility(facility: Facility) {
    if (facility) {
      this.filter = new Filter('manufactureFacility', facility.id)
      this.facilityId = facility.id
    }
  }

  set workCenters(workCenters: WorkCenter[]) {
    if (workCenters && workCenters.length > 0) {
      this.filter = new Filter(
        'workCenterIds',
        workCenters.map((wc) => wc.id)
      )
    } else {
      this.data.filter.removeFilter('workCenterIds')
    }
    this._workCenters = workCenters
  }

  get workCenters(): WorkCenter[] {
    return this._workCenters
  }

  @Input()
  public set mexusContext(ctx: MexusContext) {
    this.facility = ctx?.facility
    this.completionInterval = ctx?.interval
    this.showEvalElements = false
  }

  public set filter(filter: Filter) {
    this.filters = Array.of(filter)
  }

  public set filters(filters: Filter[]) {
    this.data.filter.updateFilters(filters)
  }

  public get completionInterval(): Interval {
    return this.reportService.completionInterval
  }

  public set completionInterval(interval: Interval) {
    this.reportService.completionInterval = interval
  }

  public get evalVariant(): EvalVariant {
    return this._evalVariant$.getValue()
  }

  public get evalVariant$(): Observable<EvalVariant> {
    return this._evalVariant$.asObservable()
  }

  public set isByDayVariant(byDay: boolean) {
    if (byDay) {
      this._evalVariant$.next(EvalVariant.BYDAY)
    } else {
      this._evalVariant$.next(EvalVariant.DEFAULT)
    }
  }

  public get isByDayVariant(): boolean {
    return this.evalVariant === EvalVariant.BYDAY
  }

  public get isByAllWorkCenters(): boolean {
    return this.evalVariant === EvalVariant.DEFAULT
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildRows(records: WorkCenterUtilizationReport[]): WorkCenterUtilizationReport[] {
    let aggRecords = records

    if (this.isByAllWorkCenters) {
      aggRecords = this.reportService.groupByField(records, 'workCenterId')
    }

    return aggRecords
  }

  calculateTotals(selected: WorkCenterUtilizationReport[]): void {
    this.totalPieceQuantity = selected.map((r) => r.totalProducedProduct).reduce(sum, 0)
    this.totalItemQuantity = selected.map((r) => r.totalItems).reduce(sum, 0)
    this.totalWeight = this.quantityService.sum(selected.map((r) => r.totalWeight))
  }

  changeProcessPlans(plans: ProcessPlan[]) {
    this.filter = WorkItemFilter.processPlans(plans)
  }

  facilitySuggestion(facility?: string) {
    return facility ? [new Filter('facility', facility)] : []
  }

  public round(pieceQuantity: number): number {
    return roundQuantity(pieceQuantity)
  }
}
